import {
  subscribeToConfig,
  LeagueConfigError,
  getConfig,
} from '@leagueplatform/config';
import { FetchAsPromise } from './fetch-as-promise';
import { callListenersWithMessage } from './subscribe-to-message';

let fetchAsPromise: FetchAsPromise;
let fetchAsPromiseAnonymous: FetchAsPromise;

/**
 * Recreates fresh WebSocket connections. Used by both the config subscription
 * (when the URL changes) and BFCache restore (when the page is rehydrated).
 */
function recreateSockets(url: string) {
  if (fetchAsPromise) {
    fetchAsPromise.unload(false);
  }

  if (fetchAsPromiseAnonymous) {
    fetchAsPromiseAnonymous.unload(false);
  }

  fetchAsPromise = new FetchAsPromise(url);
  fetchAsPromiseAnonymous = new FetchAsPromise(url, {
    anonymous: true,
  });

  fetchAsPromise.onUnpackedMessage.addListener((message) => {
    callListenersWithMessage(message);
  });
}

export const SocketAsFetch = {
  status: () => {
    if (!fetchAsPromise) {
      throw new LeagueConfigError(
        'SocketAsFetch cannot be used without the `core.api.wsUrl` config property being set.',
      );
    }

    return fetchAsPromise.lifecycleStatus;
  },
  fetch: async (...args: Parameters<FetchAsPromise['fetch']>) => {
    if (!fetchAsPromise) {
      throw new LeagueConfigError(
        'SocketAsFetch cannot be used without the `core.api.wsUrl` config property being set.',
      );
    }

    return fetchAsPromise.fetch(...args);
  },

  updateUserConnectionConfiguration: () => {
    if (!fetchAsPromise) {
      throw new LeagueConfigError(
        'SocketAsFetch cannot be used without the `core.api.wsUrl` config property being set.',
      );
    }

    return fetchAsPromise.configureConnection();
  },

  /**
   * Cleanly closes WebSocket connections when the page is about to be frozen
   * for BFCache. This ensures connections don't remain in a half-open state.
   */
  freezeConnections: async () => {
    if (fetchAsPromise) {
      await fetchAsPromise.unload();
    }
    if (fetchAsPromiseAnonymous) {
      await fetchAsPromiseAnonymous.unload();
    }
  },

  /**
   * Recreates fresh WebSocket connections after the page is restored from BFCache.
   * This reuses the same battle-tested logic as URL config changes.
   */
  restoreConnections: async () => {
    const configResult = getConfig();
    const url = configResult?.config?.core?.api?.wsUrl;

    if (!url) {
      throw new LeagueConfigError(
        'Cannot restore WebSocket connections without the `core.api.wsUrl` config property being set.',
      );
    }

    recreateSockets(url);
  },
};

export const SocketAsFetchAnonymous = {
  fetch: async (...args: Parameters<FetchAsPromise['fetch']>) => {
    if (!fetchAsPromiseAnonymous) {
      throw new LeagueConfigError(
        'SocketAsFetchAnonymous cannot be used without the `core.api.wsUrl` config property being set.',
      );
    }

    return fetchAsPromiseAnonymous.fetch(...args);
  },
};

subscribeToConfig(
  ({ config }) => config.core.api.wsUrl,
  (url) => {
    if (!url) {
      return;
    }
    recreateSockets(url);
  },
);
